import { type ReactNode } from 'react';
import { ArrowLeft } from 'lucide-react';
import { Container } from './Container';
import { Header } from './Header';
import { PageTransition } from './PageTransition';

interface ToolPageLayoutProps {
  children: ReactNode;
}

/**
 * Layout pour les pages outils (funnels, simulateurs, calculateurs).
 *
 * Garde le Header complet (logo, menu Outils, profil, CTA principal) pour
 * que l'utilisateur puisse toujours naviguer / changer de profil pendant
 * un parcours. Pas de footer plein : footer réduit avec mentions légales.
 */
export function ToolPageLayout({ children }: ToolPageLayoutProps) {
  return (
    <div className="flex min-h-screen flex-col bg-stone-50/40">
      <Header />

      <main id="main" className="flex-1">
        <PageTransition>{children}</PageTransition>
      </main>

      {/* ── Footer minimal ───────────────────────────────────── */}
      <footer className="mt-auto border-t border-stone-200/60 bg-white py-4">
        <Container className="flex flex-wrap items-center justify-between gap-3 text-[11.5px] text-stone-400">
          <span>© {new Date().getFullYear()} Solution Éco France</span>
          <nav aria-label="Liens légaux" className="flex flex-wrap gap-4">
            <a href="/legal/mentions-legales" className="transition-colors hover:text-stone-600">
              Mentions légales
            </a>
            <a href="/legal/politique-confidentialite" className="transition-colors hover:text-stone-600">
              Confidentialité
            </a>
            <a href="/legal/cookies" className="transition-colors hover:text-stone-600">
              Cookies
            </a>
            <a href="/contact" className="transition-colors hover:text-stone-600">
              Aide
            </a>
          </nav>
        </Container>
      </footer>
    </div>
  );
}

interface ToolPageSubHeaderProps {
  /** Label de l'outil affiché dans la sous-barre (ex: "Simulateur d'aides à la rénovation") */
  toolLabel: string;
  /** URL du lien "Retour". Si absent, utilise window.history.back(). */
  backHref?: string;
  /** Label du lien retour. Défaut : "Retour" */
  backLabel?: string;
  /** Bloc optionnel à afficher à droite (durée estimée, étape, etc.) */
  rightSlot?: ReactNode;
}

/**
 * Sous-barre contextuelle à placer en haut du contenu d'une page outil.
 * Affiche le nom de l'outil + un lien retour. Optionnelle.
 *
 * @example
 * <ToolPageSubHeader toolLabel="Simulateur d'aides" backHref="/renovation" />
 */
export function ToolPageSubHeader({
  toolLabel,
  backHref,
  backLabel = 'Retour',
  rightSlot,
}: ToolPageSubHeaderProps) {
  return (
    <div className="border-b border-stone-200/60 bg-white/60 backdrop-blur-sm">
      <Container className="flex h-11 items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          {backHref ? (
            <a
              href={backHref}
              className="inline-flex h-full shrink-0 items-center gap-1.5 rounded-md px-2.5 text-[12.5px] font-medium text-stone-500 transition-colors hover:bg-stone-100 hover:text-stone-700"
            >
              <ArrowLeft className="h-3.5 w-3.5" strokeWidth={2} />
              <span className="hidden sm:inline">{backLabel}</span>
            </a>
          ) : (
            <button
              type="button"
              onClick={() =>
                window.history.length > 1 ? window.history.back() : (window.location.href = '/')
              }
              className="inline-flex h-full shrink-0 items-center gap-1.5 rounded-md px-2.5 text-[12.5px] font-medium text-stone-500 transition-colors hover:bg-stone-100 hover:text-stone-700"
            >
              <ArrowLeft className="h-3.5 w-3.5" strokeWidth={2} />
              <span className="hidden sm:inline">{backLabel}</span>
            </button>
          )}
          <span aria-hidden className="h-3 w-px shrink-0 bg-stone-200" />
          <span className="truncate text-[12.5px] font-medium text-stone-700">{toolLabel}</span>
        </div>
        {rightSlot && <div className="flex shrink-0 items-center gap-2">{rightSlot}</div>}
      </Container>
    </div>
  );
}
